import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './ChatWidget.css';

/**
 * Floating AI-chat-widget på stats-sidan.
 * Renderas bara när:
 * - SHOW_CHAT-flaggan är på (Admin → Feature flags)
 * - Användaren är inloggad och har chat.use-permission (eller är owner)
 *
 * Kräver att backend har ANTHROPIC_API_KEY satt och att cs2-mcp-public
 * är nåbart via MCP_PUBLIC_URL.
 */
export default function ChatWidget() {
  const { t } = useTranslation();
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [available, setAvailable] = useState(null); // null = pending, false = hidden
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn) {
      setAvailable(false);
      return;
    }
    fetch(`${API_URL}/api/chat/availability`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => setAvailable(!!data.available))
      .catch(() => setAvailable(false));
  }, [isLoggedIn, getAuthHeaders]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const newUserMsg = { role: 'user', content: text };
    const newMessages = [...messages, newUserMsg];
    setMessages(newMessages);
    setInput('');
    setSending(true);
    setError(null);

    try {
      const r = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: messages.slice(-10),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setMessages([
        ...newMessages,
        {
          role: 'assistant',
          content: data.reply,
          tools: data.tools_called,
          usage: data.usage,
          pending_approval: data.pending_approval,
        },
      ]);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSending(false);
    }
  };

  const handleApprove = async (audit_id, msgIndex) => {
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/chat/approve`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ audit_id }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setMessages((prev) => {
        const copy = [...prev];
        copy[msgIndex] = { ...copy[msgIndex], pending_approval: null, approved: true };
        return [
          ...copy,
          {
            role: 'assistant',
            content: `${t('chat.commandRun')}\n\n${data.response || JSON.stringify(data, null, 2)}`,
            tools: [],
          },
        ];
      });
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  const handleCancel = (msgIndex) => {
    setMessages((prev) => {
      const copy = [...prev];
      copy[msgIndex] = { ...copy[msgIndex], pending_approval: null, cancelled: true };
      return [
        ...copy,
        {
          role: 'assistant',
          content: t('chat.cancelled'),
          tools: [],
        },
      ];
    });
  };

  if (available === null || available === false) return null;

  return (
    <div className={`chat-widget ${open ? 'chat-widget-open' : 'chat-widget-closed'}`}>
      {!open && (
        <button
          className="chat-toggle-btn"
          onClick={() => setOpen(true)}
          title={t('chat.openTitle')}
        >
          <span className="chat-toggle-icon">🤖</span>
          <span className="chat-toggle-label">{t('chat.aiChat')}</span>
        </button>
      )}

      {open && (
        <div className="chat-panel">
          <div className="chat-header">
            <span className="chat-header-title">Partyfabriken AI</span>
            <button
              className="chat-close-btn"
              onClick={() => setOpen(false)}
              title={t('profile.close')}
            >
              ✕
            </button>
          </div>

          <div className="chat-messages" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="chat-empty">
                {t('chat.empty')}
              </div>
            )}
            {messages.map((m, i) => {
              return (
                <div key={i} className={`chat-msg chat-msg-${m.role}`}>
                  <div className="chat-msg-content">{m.content}</div>
                  {m.pending_approval && (
                    <div className="chat-approval-box">
                      <div className="chat-approval-header">
                        ⚠ {t('chat.confirmRequired')}
                      </div>
                      <div className="chat-approval-body">
                        <div className="chat-approval-label">{t('chat.proposedCommand')}</div>
                        <code className="chat-approval-cmd">
                          {m.pending_approval.tool_name}({JSON.stringify(m.pending_approval.proposed_input)})
                        </code>
                      </div>
                      <div className="chat-approval-actions">
                        <button
                          className="chat-approval-btn chat-approval-btn-confirm"
                          onClick={() => handleApprove(m.pending_approval.audit_id, i)}
                        >
                          ✓ {t('chat.runCommand')}
                        </button>
                        <button
                          className="chat-approval-btn chat-approval-btn-cancel"
                          onClick={() => handleCancel(i)}
                        >
                          ✗ {t('matchDetail.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {sending && (
              <div className="chat-msg chat-msg-assistant chat-msg-loading">
                <span className="chat-typing">●●●</span>
              </div>
            )}
            {error && <div className="chat-error">⚠ {error}</div>}
          </div>

          <form className="chat-input-row" onSubmit={handleSend}>
            <input
              type="text"
              className="chat-input"
              placeholder={t('chat.inputPlaceholder')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending}
              maxLength={2000}
            />
            <button
              type="submit"
              className="chat-send-btn"
              disabled={sending || !input.trim()}
            >
              ➤
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
